/**
 * src/components/layout/Navbar.tsx
 * Luxury top navigation bar with brand, theme switcher, and user chip
 */

import React from 'react';
import { Sun, Moon, LogOut, Bell, Shield, Layers, ArrowLeftRight, Clock, Users, Tag } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { NotificationDrawer } from '../notifications/NotificationDrawer';
import { GlassButton } from '../ui/GlassButton';
import { haptic, playTapSound } from '../../utils/audioHaptics';

interface NavbarProps {
  currentTab: string;
  onTabChange: (tab: string) => void;
  pendingApprovalsCount?: number;
}

export const Navbar: React.FC<NavbarProps> = ({
  currentTab,
  onTabChange,
  pendingApprovalsCount = 0
}) => {
  const { theme, toggleTheme, activeLogo } = useTheme();
  const { user, logout } = useAuth();
  const { unreadCount, isDrawerOpen, setIsDrawerOpen } = useNotifications();

  return (
    <header
      style={{
        height: 'var(--header-height)',
        borderBottom: 'var(--border-hairline)',
        background: 'var(--surface-glass)',
        backdropFilter: 'blur(40px) saturate(200%)',
        WebkitBackdropFilter: 'blur(40px) saturate(200%)',
        position: 'sticky',
        top: 0,
        zIndex: 90,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <div className="container flex-between">
        {/* Brand Logo - Apple Squircle */}
        <div
          onClick={() => {
            haptic.tap();
            playTapSound();
            onTabChange('dashboard');
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '11px',
              overflow: 'hidden',
              boxShadow: '0 4px 14px rgba(10, 132, 255, 0.35)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--surface-primary)',
              flexShrink: 0
            }}
          >
            <img
              src={activeLogo}
              alt="CKAudit Logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>

          <div>
            <div style={{
              fontSize: '1.125rem',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{ color: 'var(--text-primary)' }}>CKAudit</span>
            </div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', letterSpacing: '-0.01em', fontWeight: 500 }}>
              Audit & Settlement Ledger
            </div>
          </div>
        </div>

        {/* Desktop Navigation Links - iOS Segmented Capsule */}
        <nav
          className="desktop-nav ios-segmented-control"
          style={{
            padding: '4px',
            gap: '2px'
          }}
        >
          <button
            onClick={() => {
              haptic.tap();
              playTapSound();
              onTabChange('dashboard');
            }}
            className={`ios-segment-button ${currentTab === 'dashboard' ? 'active' : ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Layers size={14} />
            <span>Wallet</span>
          </button>

          <button
            onClick={() => {
              haptic.tap();
              playTapSound();
              onTabChange('ledger');
            }}
            className={`ios-segment-button ${currentTab === 'ledger' ? 'active' : ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <ArrowLeftRight size={14} />
            <span>Ledger</span>
          </button>

          <button
            onClick={() => {
              haptic.tap();
              playTapSound();
              onTabChange('approvals');
            }}
            className={`ios-segment-button ${currentTab === 'approvals' ? 'active' : ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}
          >
            <Clock size={14} />
            <span>{user?.role === 'admin' ? 'Approvals' : 'Requests'}</span>
            {pendingApprovalsCount > 0 && (
              <span style={{
                padding: '1px 6px',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--ios-orange)',
                color: '#ffffff',
                fontSize: '0.65rem',
                fontWeight: 800,
                marginLeft: '2px'
              }}>
                {pendingApprovalsCount}
              </span>
            )}
          </button>

          {user?.role === 'admin' && (
            <>
              <button
                onClick={() => {
                  haptic.tap();
                  playTapSound();
                  onTabChange('users');
                }}
                className={`ios-segment-button ${currentTab === 'users' ? 'active' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Users size={14} />
                <span>Accounts</span>
              </button>

              <button
                onClick={() => {
                  haptic.tap();
                  playTapSound();
                  onTabChange('categories');
                }}
                className={`ios-segment-button ${currentTab === 'categories' ? 'active' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Tag size={14} />
                <span>Categories</span>
              </button>
            </>
          )}
        </nav>

        {/* Right Actions: Notifications, Theme Toggle & User Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Notifications Bell Button */}
          <button
            onClick={() => {
              haptic.tap();
              playTapSound();
              setIsDrawerOpen(true);
            }}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: 'var(--border-hairline)',
              background: 'rgba(118, 118, 128, 0.18)',
              color: unreadCount > 0 ? 'var(--ios-blue)' : 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              transition: 'all var(--duration-fast)'
            }}
            title="Notifications"
          >
            <Bell size={16} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                minWidth: '17px',
                height: '17px',
                padding: '0 4px',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--ios-red)',
                color: '#ffffff',
                fontSize: '0.625rem',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 8px rgba(255, 69, 58, 0.5)'
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Theme Switcher Button */}
          <button
            onClick={() => {
              haptic.tap();
              playTapSound();
              toggleTheme();
            }}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: 'var(--border-hairline)',
              background: 'rgba(118, 118, 128, 0.18)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all var(--duration-fast)'
            }}
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <Sun size={16} color="var(--ios-orange)" /> : <Moon size={16} color="var(--ios-blue)" />}
          </button>

          {/* User Profile Chip & Logout */}
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div 
                onClick={() => {
                  haptic.tap();
                  playTapSound();
                  onTabChange('profile');
                }}
                className="ios-tap-target"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '3px 12px 3px 5px',
                  borderRadius: 'var(--radius-pill)',
                  background: currentTab === 'profile' ? 'rgba(10, 132, 255, 0.22)' : 'rgba(118, 118, 128, 0.18)',
                  border: currentTab === 'profile' ? '0.5px solid var(--ios-blue)' : 'var(--border-hairline)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all var(--duration-fast)'
                }}
                title="View CK ID Profile & Settings"
              >
                <div style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: user.role === 'admin' ? 'var(--ios-blue)' : 'var(--ios-green)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.78rem'
                }}>
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.username}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    user.username.charAt(0).toUpperCase()
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.1, color: 'var(--text-primary)' }}>
                    {user.username}
                  </span>
                  <span style={{ fontSize: '0.625rem', color: 'var(--text-tertiary)', textTransform: 'capitalize' }}>
                    {user.role}
                  </span>
                </div>
              </div>

              <button
                onClick={() => {
                  haptic.button();
                  playTapSound();
                  logout();
                }}
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(255, 69, 58, 0.12)',
                  color: 'var(--ios-red)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background var(--duration-fast)'
                }}
                title="Log out"
              >
                <LogOut size={15} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Notifications Drawer */}
      <NotificationDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onNavigateTab={onTabChange}
      />

      <style>{`
        @media (max-width: 860px) {
          .desktop-nav {
            display: none !important;
          }
        }
      `}</style>
    </header>
  );
};
